/**
 * T-010 — Stage 4 execution engine + pipeline orchestrator (the apply).
 *
 * `applyViaHybridEngine` is the single entry point. It composes the whole pipeline and executes the
 * resolved ops against Word as native tracked changes:
 *
 *   read live baseline → extract protected regions (T-007) → buildApplyPlan (T-006/03/04/05/08)
 *     → pre-flight locate (T-009) → execute right-to-left → restore state
 *
 * Key properties:
 *  - **Atomic.** Execution runs only if pre-flight resolves every op; otherwise it returns
 *    `applied:false` having mutated nothing (a clean no-op, not the old partial corruption).
 *  - **Native revisions.** Track changes is forced on and edits use the native Range API
 *    (`delete` / `insertText`), not `insertOoxml` — so Word records real revisions, nests a deletion
 *    of counterparty-inserted text inside their insertion, and keeps fields/bookmarks physically
 *    intact.
 *  - **Right-to-left.** Ops apply by descending baseline offset (pre-flight already ordered them), so
 *    editing one range never shifts the document position of a range not yet edited.
 *  - **Batched syncs.** All mutations are queued and flushed together — not one sync per op.
 *
 * The pure composition is `buildApplyPlan` (unit-tested). This module is the Word-facing shell,
 * validated by hand in real Word (Stage 4 is manual by design).
 */

import { buildApplyPlan } from './buildApplyPlan';
import { createProtectedRegionSet } from './protectedRegions';
import { extractProtectedRegions } from './extractProtectedRegions';
import { extractRevisionsForNormalizedBaseline } from './extractRevisions';
import { debugHybridPipeline } from './debugPipeline';
import { preflightLocate } from './preflightLocate';
import { unsearchableCharCodesOf } from './locateOps';
import { RevisionContext } from './types';
import type { SubmitRevertContext, TrackedChangeFingerprint } from '../../word/trackChanges/RedlineInserter';
import { normalizeWordParagraphBaseline } from '../../utils/bidiText';
import {
  logQuickResponseBidiCompare,
  logQuickResponseStage,
  qrTextSnapshot,
} from '../../utils/quickResponseDebug';

/* global Word */

/**
 * Snapshot the paragraph's tracked-change fingerprints (date+type) for undo matching. Author-free —
 * Office.js stamps native edits with the live user, so undo relies on fingerprints + an apply-time
 * window, not authorship. Mirrors RedlineInserter.collectTrackedChangeFingerprints so the hybrid and
 * legacy undo paths use the same signal. Never throws (Win32 can reject getTrackedChanges).
 */
async function collectFingerprints(
  paragraph: Word.Paragraph,
  context: Word.RequestContext,
): Promise<TrackedChangeFingerprint[]> {
  try {
    const tcs = (paragraph as unknown as {
      getTrackedChanges: () => { items: Array<{ load: (p: string[]) => void; date?: Date | string; type?: string }> };
    }).getTrackedChanges();
    (tcs as unknown as { load: (p: string) => void }).load('items');
    await context.sync();
    const items = tcs.items || [];
    for (const tc of items) tc.load(['date', 'type']);
    await context.sync();
    return items.map((tc) => ({
      dateIso: tc.date ? new Date(tc.date).toISOString() : '',
      type: String(tc.type ?? ''),
    }));
  } catch {
    return [];
  }
}

/** Fingerprints present in `after` beyond `before` (count-aware) — the changes this apply added. */
function addedFingerprints(
  before: TrackedChangeFingerprint[],
  after: TrackedChangeFingerprint[],
): TrackedChangeFingerprint[] {
  const key = (f: TrackedChangeFingerprint) => `${f.dateIso}|${f.type}`;
  const beforeCount = new Map<string, number>();
  for (const f of before) beforeCount.set(key(f), (beforeCount.get(key(f)) ?? 0) + 1);
  const added: TrackedChangeFingerprint[] = [];
  for (const f of after) {
    const c = beforeCount.get(key(f)) ?? 0;
    if (c > 0) beforeCount.set(key(f), c - 1);
    else added.push(f);
  }
  return added;
}

export interface HybridApplyOptions {
  /** Pre-existing revisions in the paragraph (visible-baseline coords). Default: none. */
  revisionContext?: RevisionContext;
}

/** Structural, text-free trace of an apply attempt (T-012 formalizes/guards this). */
export interface HybridApplyTrace {
  baselineLength: number;
  targetLength: number;
  ops: { keep: number; delete: number; insert: number };
  protectedRegions: { field: number; bookmark: number; total: number };
  protectedOpCount: number;
  preflight: {
    ok: boolean;
    total: number;
    resolved: number;
    failures: Array<{ kind: string; reason: string }>;
  };
  /** Number of ops skipped (field-protected / unsearchable / not found) — applied the rest. */
  skipped: number;
  /** Number of whole fields deleted because a redline fully spanned them. */
  fieldDeletes: number;
  /** Distinct char codes that made some op's search value unsearchable (diagnostics; codes only). */
  unsearchableCodes: number[];
  syncCount: number;
  outcome: 'applied' | 'applied_partial' | 'nothing_to_apply' | 'error';
}

export interface HybridApplyResult {
  applied: boolean;
  reason?: string;
  trace: HybridApplyTrace;
  /** Undo context: fingerprints + time window of the changes this apply added (null if nothing applied). */
  revertContext?: SubmitRevertContext;
}

/**
 * Apply a clean target to one paragraph via the hybrid engine, as native tracked changes.
 *
 * @param paragraphIndex 0-based index of the target paragraph in the document body
 * @param cleanTarget    the AI/manual clean accepted-state clause
 */
export async function applyViaHybridEngine(
  paragraphIndex: number,
  cleanTarget: string,
  options: HybridApplyOptions = {},
): Promise<HybridApplyResult> {
  let syncCount = 0;

  // A single mutable trace, updated as the pipeline progresses. On any thrown error the catch
  // returns THIS (not a blank), so the log still shows how far we got — baseline length, op counts,
  // protected regions, pre-flight outcome. That is what a "no-op" needs to be diagnosable.
  const trace: HybridApplyTrace = {
    baselineLength: 0,
    targetLength: cleanTarget.length,
    ops: { keep: 0, delete: 0, insert: 0 },
    protectedRegions: { field: 0, bookmark: 0, total: 0 },
    protectedOpCount: 0,
    preflight: { ok: false, total: 0, resolved: 0, failures: [] },
    skipped: 0,
    fieldDeletes: 0,
    unsearchableCodes: [],
    syncCount: 0,
    outcome: 'error',
  };

  try {
    return await Word.run(async (context) => {
      const sync = async () => {
        syncCount += 1;
        await context.sync();
      };

      // Resolve the paragraph and read the live visible baseline.
      const paragraphs = context.document.body.paragraphs;
      paragraphs.load('items');
      await sync();

      const rawParagraph = paragraphs.items[paragraphIndex];
      if (!rawParagraph) {
        trace.syncCount = syncCount;
        return { applied: false, reason: 'paragraph_not_found', trace };
      }
      // The office-js typings in this repo infer paragraphs.items as Range[]; the runtime objects are
      // genuinely Word.Paragraph. Cast once here so the seam signatures stay honest.
      const paragraph = rawParagraph as unknown as Word.Paragraph;

      const whole = paragraph.getRange('Whole');
      whole.load('text');
      // Snapshot the pre-apply paragraph OOXML for a DETERMINISTIC undo — restoring this exact
      // package puts the paragraph back including counterparty revisions, no fuzzy tc.reject matching.
      const originalOoxml = whole.getOoxml();
      await sync();
      const rawBaseline = whole.text || '';
      const baselineVisible = normalizeWordParagraphBaseline(rawBaseline);
      const originalParagraphOoxmlPackage = originalOoxml.value || '';
      trace.baselineLength = baselineVisible.length;

      // Protected regions. Only FIELDS block edits (their result is generated content we must not
      // touch). BOOKMARKS are advisory: on this native search/delete path Word preserves bookmark
      // ranges across edits (unlike the old wholesale-OOXML rewrite), so blocking every edit that
      // overlaps one would needlessly veto almost the whole paragraph. We still report bookmark
      // counts for observability.
      const extracted = await extractProtectedRegions(context, paragraph);
      // extractProtectedRegions performs its own sync(s); count one representative flush.
      syncCount += 1;
      trace.protectedRegions = createProtectedRegionSet(extracted.map((e) => e.region)).count();

      // Field regions sorted the SAME way createProtectedRegionSet sorts, so a fieldDelete op's
      // fieldRegionIndex (an index into protectedSet.regions) aligns with fieldRanges below.
      const fieldEntries = extracted
        .filter((e) => e.region.kind === 'field')
        .sort((a, b) => a.region.start - b.region.start || a.region.end - b.region.end);
      const protectedSet = createProtectedRegionSet(fieldEntries.map((e) => e.region));
      const fieldRanges = fieldEntries.map((e) => e.range);
      // Keep the field range proxies alive across syncs into the apply pass.
      for (const fr of fieldRanges) {
        if (fr) (context.trackedObjects.add as (o: Word.Range) => void)(fr);
      }

      // Existing tracked revisions — so the engine preserves counterparty edits instead of re-diffing
      // them into new our-side edits. Caller override wins (for tests); otherwise read from the doc.
      const revisionContext =
        options.revisionContext ??
        (await extractRevisionsForNormalizedBaseline(
          context,
          paragraph,
          rawBaseline,
          baselineVisible,
        ));
      syncCount += 1;

      // Pure plan.
      const plan = buildApplyPlan(baselineVisible, cleanTarget, protectedSet, revisionContext);
      debugHybridPipeline('apply', baselineVisible, cleanTarget, revisionContext, plan.targetVisible, plan.ops);
      const insertOps = plan.ops.filter((op) => op.kind === 'insert');
      logQuickResponseStage('hybrid apply: plan built', {
        paragraphIndex,
        baselineVisible: qrTextSnapshot('baselineVisible', baselineVisible),
        cleanTarget: qrTextSnapshot('cleanTarget', cleanTarget),
        targetVisible: qrTextSnapshot('targetVisible', plan.targetVisible),
        opHistogram: plan.histogram,
        insertOpCount: insertOps.length,
        insertOps: insertOps.map((op) => qrTextSnapshot('insertLogical', op.value)),
        locatedInsertCount: plan.located.filter((l) => l.kind === 'insert').length,
      });
      for (const op of insertOps) {
        if (/[\[\](){}]/.test(op.value)) {
          logQuickResponseBidiCompare('hybrid apply plan insert (logical)', op.value);
        }
      }
      trace.targetLength = plan.targetVisible.length;
      trace.ops = plan.histogram;
      trace.fieldDeletes = plan.located.filter((l) => l.kind === 'fieldDelete').length;
      // Diagnostics: which char codes (if any) make an op's search value unsearchable.
      const codes = new Set<number>();
      for (const l of plan.located) {
        const value = l.kind === 'delete' ? l.value : l.kind === 'insert' && l.anchor.type !== 'start' && l.anchor.type !== 'end' ? l.anchor.value : '';
        for (const c of unsearchableCharCodesOf(value)) codes.add(c);
      }
      trace.unsearchableCodes = [...codes].sort((a, b) => a - b);
      trace.protectedOpCount = plan.protectedOpCount;
      trace.preflight = { ok: false, total: plan.located.length, resolved: 0, failures: [] };

      // Pre-flight: resolve everything read-only, or abort.
      const { plan: preflightPlan, ranges } = await preflightLocate(context, paragraph, plan.located);
      syncCount += 1; // preflightLocate's single batched search sync
      trace.preflight = {
        ok: preflightPlan.ok,
        total: preflightPlan.total,
        resolved: preflightPlan.resolvedCount,
        failures: preflightPlan.failures.map((f) => ({ kind: f.kind, reason: f.reason })),
      };
      trace.skipped = preflightPlan.failures.length;

      // Nothing resolved → there is genuinely nothing safe to apply.
      if (ranges.length === 0) {
        trace.outcome = 'nothing_to_apply';
        trace.syncCount = syncCount;
        return { applied: false, reason: 'nothing_to_apply', trace };
      }

      // Snapshot existing tracked-change fingerprints so undo can tell OUR new changes from the
      // counterparty's (author-free — Office.js stamps native edits with the live user).
      const beforeFingerprints = await collectFingerprints(paragraph, context);
      syncCount += 2; // collectFingerprints' two loads
      const applyStartedAtMs = Date.now();

      // Force track changes on, remembering the prior mode to restore it.
      const doc = context.document;
      doc.load('changeTrackingMode');
      await sync();
      const priorMode = doc.changeTrackingMode;
      doc.changeTrackingMode = Word.ChangeTrackingMode.trackAll;

      // Execute right-to-left (ranges are already ordered by descending offset). Queue all
      // mutations, then flush once.
      for (const resolved of ranges) {
        const op = resolved.op;
        if (op.kind === 'delete') {
          if (resolved.range) resolved.range.delete();
        } else if (op.kind === 'fieldDelete') {
          // Delete the whole field via its own range (a redline fully spans it).
          const fr = fieldRanges[op.fieldRegionIndex];
          if (fr) fr.delete();
        } else {
          // insert — placement depends on the anchor kind
          logQuickResponseStage('hybrid apply: executing insert', {
            paragraphIndex,
            anchorType: op.anchor.type,
            insertValue: qrTextSnapshot('insertValue', op.value),
          });
          if (/[\[\](){}]/.test(op.value)) {
            logQuickResponseBidiCompare('hybrid apply insertText arg', op.value);
          }
          if (op.anchor.type === 'start') {
            paragraph.insertText(op.value, Word.InsertLocation.start);
          } else if (op.anchor.type === 'end') {
            paragraph.insertText(op.value, Word.InsertLocation.end);
          } else if (resolved.range) {
            const where =
              op.anchor.type === 'after' ? Word.InsertLocation.after : Word.InsertLocation.before;
            resolved.range.insertText(op.value, where);
          }
        }
      }
      await sync(); // single batched flush of all mutations

      // Restore the caller's prior tracking mode and release tracked proxies.
      doc.changeTrackingMode = priorMode;
      for (const resolved of ranges) {
        if (resolved.range) {
          (context.trackedObjects.remove as (o: Word.Range) => void)(resolved.range);
        }
      }
      for (const fr of fieldRanges) {
        if (fr) (context.trackedObjects.remove as (o: Word.Range) => void)(fr);
      }
      await sync();

      // Capture the fingerprints our edits added, plus the apply window, for undo.
      const afterFingerprints = await collectFingerprints(paragraph, context);
      syncCount += 2;
      const revertContext: SubmitRevertContext = {
        applyStartedAtMs,
        applyFinishedAtMs: Date.now(),
        addedFingerprints: addedFingerprints(beforeFingerprints, afterFingerprints),
        paragraphIndex,
        // Deterministic undo: restore the exact pre-apply paragraph (counterparty edits preserved)
        // rather than fuzzy-rejecting a subset of our native tracked-change segments.
        preferOriginalRestore: true,
        originalParagraphText: baselineVisible,
        ...(originalParagraphOoxmlPackage
          ? { originalParagraphOoxmlPackage }
          : {}),
      };

      trace.outcome = preflightPlan.ok ? 'applied' : 'applied_partial';
      trace.syncCount = syncCount;
      return {
        applied: true,
        reason: preflightPlan.ok ? undefined : `partial_apply_skipped_${trace.skipped}`,
        trace,
        revertContext,
      };
    });
  } catch (err) {
    // Preserve whatever the trace captured before the throw; only stamp outcome + sync count.
    trace.outcome = 'error';
    trace.syncCount = syncCount;
    return {
      applied: false,
      reason: err instanceof Error ? err.message : String(err),
      trace,
    };
  }
}
